import { motion, AnimatePresence } from 'motion/react';

import type { MemberBody } from '@/shared/api/generated/model';
import { springs } from '@/shared/lib/motion';
import { Avatar } from '@/shared/ui/Avatar';
import { Tooltip } from '@/shared/ui/Tooltip';

import styles from './AvatarStack.module.scss';

interface Props {
  /** Кого показать; порядок — как пришёл. */
  ids: string[];
  members: MemberBody[];
  /** Кого не показывать — обычно себя. */
  exclude?: string | undefined;
  max?: number;
  size?: 'xs' | 'sm';
  label: (name: string) => string;
}

/**
 * Стопка аватаров с наложением: кто онлайн, кто смотрит задачу. Появление и
 * уход — пружиной, имя — в подсказке.
 */
export function AvatarStack({ ids, members, exclude, max = 5, size = 'xs', label }: Props) {
  const people = ids
    .filter((id) => id !== exclude)
    .map((id) => members.find((m) => m.user_id === id))
    .filter((m): m is MemberBody => m !== undefined);
  const shown = people.slice(0, max);
  const rest = people.length - shown.length;

  return (
    <span className={styles['stack']}>
      <AnimatePresence initial={false}>
        {shown.map((m) => {
          const name = m.name === '' ? m.email : m.name;

          return (
            <motion.span
              key={m.user_id}
              layout
              className={styles['stack__item']}
              initial={{ scale: 0.4, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.4, opacity: 0 }}
              transition={springs.rubber}
            >
              <Tooltip content={label(name)}>
                <span>
                  <Avatar name={name} size={size} online />
                </span>
              </Tooltip>
            </motion.span>
          );
        })}
      </AnimatePresence>
      {rest > 0 && <span className={styles['stack__more']}>+{rest}</span>}
    </span>
  );
}
